import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus, ExternalLink, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { btnLine, dmy, fieldAuto } from "@/components/stable/ui";

const Card = ({ c, horses, user, inCal }) => {
  const [hid, setHid] = useState("");
  const [added, setAdded] = useState(false);
  const isAdded = added || inCal;
  const add = async () => {
    try { await api.post(`/competitions/${c.id}/add`, null, { params: hid ? { horse_id: hid } : {} }); setAdded(true); toast.success("Añadido a tu calendario con su lista de documentación"); } catch (e) { toast.error(errMsg(e)); }
  };
  const [d, m] = [c.date.slice(8, 10), new Date(`${c.date}T12:00:00`).toLocaleDateString("es-ES", { month: "short" })];
  return (
    <article className="border border-slate-200 rounded p-5 grid grid-cols-[72px_1fr] gap-5" data-testid={`competition-${c.id}`}>
      <div className="bg-[#0B1D3A] text-white rounded text-center py-3 h-fit"><p className="font-display font-black text-3xl leading-none">{d}</p><p className="text-xs uppercase tracking-widest mt-1">{m}</p></div>
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-[#DC2626]">{[c.discipline, c.level].filter(Boolean).join(" · ")}</p>
        <h3 className="font-display font-bold text-xl text-[#0B1D3A]">{c.title}</h3>
        <p className="text-sm text-slate-600 flex items-center gap-1.5 mt-1"><MapPin size={14} /> {c.location || "Por confirmar"} · {dmy(c.date)}{c.end_date ? ` al ${dmy(c.end_date)}` : ""}</p>
        {c.deadline && <p className="text-sm text-[#D97706] font-semibold mt-1">Cierre de inscripción: {dmy(c.deadline)}</p>}
        {c.notes && <p className="text-sm text-slate-500 mt-2">{c.notes}</p>}
        <div className="flex flex-wrap items-center gap-2 mt-4">
          {c.link && <a href={c.link} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-[#2563EB] inline-flex items-center gap-1" data-testid={`competition-link-${c.id}`}>Inscripción / bases <ExternalLink size={13} /></a>}
          {user ? (isAdded ? <Link to="/cuadra?tab=calendario" className="text-sm font-bold text-[#16A34A]" data-testid={`competition-added-${c.id}`}>En tu calendario ✓</Link> : <>
            {horses.length > 0 && <select value={hid} onChange={(e) => setHid(e.target.value)} className={`${fieldAuto} h-10`} data-testid={`competition-horse-${c.id}`}><option value="">Sin caballo</option>{horses.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}</select>}
            <button onClick={add} className={`${btnLine} text-sm`} data-testid={`competition-add-${c.id}`}><CalendarPlus size={16} /> Añadir a mi calendario</button>
          </>) : <Link to="/login?next=/concursos" className="text-sm font-bold text-[#DC2626]">Inicia sesión para añadirlo a tu calendario</Link>}
        </div>
      </div>
    </article>
  );
};

export default function Competitions() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [horses, setHorses] = useState([]);
  const [disc, setDisc] = useState("");
  const [inCal, setInCal] = useState(new Set());
  useEffect(() => { api.get("/competitions").then((r) => setList(r.data)); }, []);
  useEffect(() => { if (user) { api.get("/horses").then((r) => setHorses(r.data)); api.get("/events").then((r) => setInCal(new Set(r.data.filter((e) => e.kind === "concurso" && e.competition_id).map((e) => e.competition_id)))); } }, [user]);
  const discs = [...new Set((list || []).map((c) => c.discipline).filter(Boolean))];
  const shown = (list || []).filter((c) => !disc || c.discipline === disc);
  return (
    <div data-testid="competitions-page">
      <section className="bg-[#0B1D3A] text-white">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#93C5FD] mb-3">Modo competición</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase">Calendario de concursos</h1>
          <p className="text-blue-100 mt-3 max-w-2xl">Añade un concurso a tu calendario y te recordaremos el cierre de inscripción, la documentación pendiente y el día de la prueba.</p>
        </div>
      </section>
      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex flex-wrap gap-3 items-center mb-6">
          {discs.length > 1 && <select value={disc} onChange={(e) => setDisc(e.target.value)} className={fieldAuto} data-testid="competition-filter"><option value="">Todas las disciplinas</option>{discs.map((d) => <option key={d}>{d}</option>)}</select>}
          {user && <Link to="/cuadra?tab=calendario" className="text-sm font-bold text-[#2563EB] underline underline-offset-4">¿Tu concurso no está? Añádelo en Mi cuadra</Link>}
        </div>
        {list && !shown.length && <p className="text-slate-500" data-testid="competitions-empty">Todavía no hay concursos publicados. {user ? "Puedes añadir los tuyos en Mi cuadra → Calendario." : ""}</p>}
        <div className="space-y-4">{shown.map((c) => <Card key={c.id} c={c} horses={horses} user={user} inCal={inCal.has(c.id)} />)}</div>
      </div>
    </div>
  );
}
